import { useSearchParams } from "react-router-dom";

import { SearchParams } from "@/shared/environment";
import { actions } from "@/shared/state/visualization";
import {
  useDatasources,
  useVizDispatch,
} from "@/shared/state/visualization/hooks";
import { type FileSource } from "@/shared/state/visualization/schema/v1";

import { useWorkspaceLoaderData } from "../loader";

/**
 * Returns `addFiles`, which merges file IDs into the open visualization
 * workspace's datasources (existing dataset, session, and file sources are
 * kept, duplicate files dropped) and rewrites the URL's dataset, session, and
 * file search params to match. A workspace opened from a shared workspace link
 * gets the merge but not the rewrite.
 *
 * Session datasources are preserved and re-emitted as `?session=`, so a reload
 * re-expands the same sessions (and the same grouping) — a session of any size
 * still costs one search param.
 */
export function useAddFilesToWorkspace() {
  const dispatch = useVizDispatch();
  const [, setSearchParams] = useSearchParams();
  const workspaceLoaderData = useWorkspaceLoaderData();
  const datasources = useDatasources();

  const isSharedWorkspace =
    workspaceLoaderData.configSource?.type === "workspace";

  const addFiles = (fileIdsToAdd: string[]) => {
    // Keep every existing dataset source — a workspace can hold several
    // (e.g. when opened from an event spanning multiple datasets).
    const datasetSources = datasources.filter((ds) => ds.type === "dataset");

    // Keep every session source untouched: the fetcher re-expands each into its
    // files and record, so the grouping survives adding a file by hand.
    const sessionSources = datasources.filter((ds) => ds.type === "session");

    const existingFileSources: FileSource[] = datasources.filter(
      (ds) => ds.type === "file",
    );

    const existingFileIds = new Set(
      existingFileSources.map((ds) => ds.value.fileId),
    );
    const addedFileSources: FileSource[] = [...new Set(fileIdsToAdd)]
      .filter((fileId) => !existingFileIds.has(fileId))
      .map((fileId) => ({ type: "file", value: { fileId } }));

    const fileSources = [...existingFileSources, ...addedFileSources];

    dispatch(
      actions.setDatasources([
        ...datasetSources,
        ...sessionSources,
        ...fileSources,
      ]),
    );

    // A workspace opened from a shared workspace link gets its contents from
    // the persisted workspace record, so dataset/session/file search params
    // don't apply to it.
    if (isSharedWorkspace) {
      return;
    }

    setSearchParams(
      (oldParams) => {
        const params = new URLSearchParams(oldParams);

        params.delete(SearchParams.DATASET);
        datasetSources.forEach((ds) => {
          params.append(SearchParams.DATASET, ds.value.datasetId);
        });

        params.delete(SearchParams.SESSION);
        sessionSources.forEach((ds) => {
          params.append(SearchParams.SESSION, ds.value.sessionId);
        });

        params.delete(SearchParams.FILE);
        fileSources.forEach((ds) => {
          params.append(SearchParams.FILE, ds.value.fileId);
        });

        return params;
      },
      { preventScrollReset: true, replace: true },
    );
  };

  return { addFiles };
}
